import i18next from 'i18next';
import React from 'react';

import { Dropdown, INPUT_WIDTH } from '@commons/utils/styledLibraryComponents';

import { Input } from '@lib/inpulse/Input';

import theme from '@theme';

import {
  formatTextNumberInputValue,
  sortCategoryAndSubCategoryDropdown,
} from '@commons/utils/format';
import utils from '../utils';

/*************************************/
/* Inputs Product Form Configuration */
/*************************************/

export const ProductNameInput = () => ({
  name: `* ${i18next.t('ADMIN.CASHIER_PRODUCTS.ASSOCIATION_MODAL_LIST_COLUMN_NAME')}`,
  keyProperty: 'name',
  component: Input,
  props: ({ product, onProductChange, isReadOnly }) => ({
    type: 'text',
    readOnly: isReadOnly,
    onChange: (event) => handleOnChangeProduct(product, onProductChange, event, 'name'),
  }),
});

export const ProductCategoryInput = () => ({
  name: i18next.t('ADMIN.PRODUCTS.INFO_CATEGORY'),
  keyProperty: 'category',
  component: Dropdown,
  props: ({ product, onProductChange, productsCategories, isReadOnly, ...props }) => {
    const selectedCategory = productsCategories.find(({ id }) => id === product.categoryId) || null;

    return {
      width: INPUT_WIDTH.FULL,
      isDisabled: isReadOnly,
      items: productsCategories,
      selectedItem: selectedCategory,
      sortBy: (items) => sortCategoryAndSubCategoryDropdown(items),
      labelStyle: { font: theme.fonts.textSmall },
      onSelectionChange: (selectedItem) => {
        if (!selectedItem) {
          onProductChange({
            ...product,
            categoryId: null,
            category: null,
          });

          return;
        }

        onProductChange({
          ...product,
          categoryId: selectedItem.id,
          category: selectedItem.name,
        });
      },
      placeholder: i18next.t('GENERAL.CHOOSE_PLACEHOLDER'),
      button: {
        id: 1,
        text: i18next.t('GENERAL.LIST_CREATE_CATEGORY'),
        handleClick: () => {
          handleCategorySubcategoryCreation('category', props);
        },
        iconSrc: '/images/inpulse/add-black-small.svg',
      },
    };
  },
});

export const ProductSubCategoryInput = () => ({
  name: `${i18next.t('GENERAL.SUB_CATEGORY')} :`,
  keyProperty: 'subCategory',
  component: Dropdown,
  props: ({ product, onProductChange, productsSubCategories, isReadOnly, ...props }) => {
    const selectedSubCategory =
      productsSubCategories.find(({ id }) => id === product.subCategoryId) || null;

    return {
      width: INPUT_WIDTH.FULL,
      isDisabled: isReadOnly,
      selectedItem: selectedSubCategory,
      items: productsSubCategories,
      sortBy: (items) => sortCategoryAndSubCategoryDropdown(items),
      labelStyle: { font: theme.fonts.textSmall },
      onSelectionChange: (selectedItem) => {
        if (!selectedItem) {
          onProductChange({
            ...product,
            subCategoryId: null,
            subCategory: null,
          });

          return;
        }

        onProductChange({
          ...product,
          subCategoryId: selectedItem.id,
          subCategory: selectedItem.name,
        });
      },
      placeholder: i18next.t('GENERAL.CHOOSE_PLACEHOLDER'),
      button: {
        id: 1,
        text: i18next.t('GENERAL.LIST_CREATE_SUBCATEGORY'),
        handleClick: () => {
          handleCategorySubcategoryCreation('subCategory', props);
        },
        iconSrc: '/images/inpulse/add-black-small.svg',
      },
    };
  },
});

export const EmptyInput = {
  name: 'empty',
  keyProperty: '',
  component: () => <div></div>,
  props: () => ({}),
};

export const ProductDlcInput = () => ({
  name: i18next.t('ADMIN.PRODUCTS.PRODUCT_SHELF_LIFE_BEST_BEFORE'),
  keyProperty: 'shelfLife',
  component: Input,
  props: ({ product, onProductChange, isReadOnly }) => ({
    type: 'number',
    readOnly: isReadOnly,
    onChange: (event) => handleOnChangeProduct(product, onProductChange, event, 'shelfLife'),
  }),
});

export const ProductChannelPriceInput = (channel, currency, defaultChannelId) => {
  const shorten = i18next.t(`${channel.translationKey}.SHORTEN`) || '';
  const full = i18next.t(`${channel.translationKey}.FULL`) || '';
  const label = `${i18next.t('GENERAL.PRICE_TTC_WITH_CHANNEL', { channel: shorten })} (${
    currency.alphabeticCode
  })`;

  const isDefaultChannel = channel.id === defaultChannelId;

  return {
    name: label,
    keyProperty: `channelPrices.${channel.id}.priceWithTaxes`,
    component: Input,
    getValue: (product) => product.channelPrices?.[channel.id]?.priceWithTaxes ?? null,
    props: ({ product, onProductChange, isProductSynced, isReadOnly }) => ({
      type: 'text',
      readOnly: (isDefaultChannel && isProductSynced) || isReadOnly,
      disabled: (isDefaultChannel && isProductSynced) || isReadOnly,
      tooltipText: i18next.t(
        'ADMIN.PRODUCTS.CREATE_PRODUCT_MODAL_CASHIER_PRODUCT_ASSOCIATION_PRICE_WITH_CHANNEL',
        { channel: full },
      ),
      onChange: (event) =>
        handleOnChangeChannelPrice(
          product,
          onProductChange,
          event,
          channel.id,
          'priceWithTaxes',
          defaultChannelId,
        ),
    }),
  };
};

export const CurrencySelector = (props) => {
  const { product, onProductChange, isDisabled, currency } = props;

  return (
    <Dropdown
      customStyle={{ marginBottom: '28px' }} // not fitting the 8px rule to match legacy page style
      isDisabled={isDisabled}
      isLabelTextSmall={true}
      isRequired={true}
      label={`${i18next.t('GENERAL.CURRENCY')} :`}
      selectedItem={currency}
      width={INPUT_WIDTH.FULL}
      onSelectionChange={(selectedItem) => {
        handleOnChangeProduct(
          product,
          onProductChange,
          {
            target: { value: selectedItem.id },
          },
          'currency',
        );
      }}
    />
  );
};

export const ProductCurrencyInput = () => ({
  keyProperty: 'currency',
  component: CurrencySelector,
  props: ({ product, onProductChange, currency }) => ({
    product,
    onProductChange,
    isDisabled: true,
    currency,
  }),
});

export const ProductChannelVatInput = (channel, defaultChannelId) => {
  const shorten = i18next.t(`${channel.translationKey}.SHORTEN`);
  const vatLabel = i18next.t('ADMIN.CASHIER_PRODUCTS.ASSOCIATION_MODAL_LIST_COLUMN_VAT_RATE');
  const label = `${vatLabel}${shorten ? ` ${shorten}` : ''} (%)`;

  const isDefaultChannel = channel.id === defaultChannelId;

  return {
    name: label,
    keyProperty: `channelPrices.${channel.id}.vatRate`,
    component: Input,
    getValue: (product) => product.channelPrices?.[channel.id]?.vatRate,
    props: ({ product, onProductChange, isProductSynced, isReadOnly }) => ({
      type: 'text',
      tooltipText: i18next.t('ADMIN.PRODUCTS.DELIVERY_VAT_RATE_TOOLTIP'),
      readOnly: (isDefaultChannel && isProductSynced) || isReadOnly,
      disabled: (isDefaultChannel && isProductSynced) || isReadOnly,
      onChange: (event) =>
        handleOnChangeChannelPrice(
          product,
          onProductChange,
          event,
          channel.id,
          'vatRate',
          defaultChannelId,
        ),
    }),
  };
};

export const ProductSkuInput = () => ({
  name: i18next.t('GENERAL.SKU'),
  keyProperty: 'sku',
  component: Input,
  props: ({ product, onProductChange, isReadOnly }) => ({
    type: 'text',
    readOnly: isReadOnly,
    onChange: (event) => handleOnChangeProduct(product, onProductChange, event, 'sku'),
  }),
});

export const ProductBrandsInput = () => ({
  name: `${i18next.t('ADMIN.STORES.LIST_COLUMN_BRAND')} :`,
  keyProperty: 'brand',
  component: Dropdown,
  props: ({ product, onProductChange, brands, isReadOnly }) => {
    const selectedBrand = brands.find((brand) => brand.name === product.brand);

    return {
      width: INPUT_WIDTH.FULL,
      labelStyle: { font: theme.fonts.textSmall },
      items: brands,
      selectedItem: selectedBrand,
      isDisabled: isReadOnly,
      onSelectionChange: (selectedItem, _, isUnselected) => {
        onProductChange({
          ...product,
          brandId: isUnselected ? null : selectedItem.id,
          brand: isUnselected ? '' : selectedItem.name,
        });
      },
    };
  },
});

/*************************/
/* Input Change Handlers */
/*************************/

export function handleBrandInput(product, onProductChange, clientBrands, selectedBrand) {
  const updatedProduct = { ...product, brandId: null, brand: null };

  const matchingBrand = clientBrands.find(
    (clientBrand) => clientBrand.name.toLowerCase() === selectedBrand.toLowerCase(),
  );

  if (matchingBrand) {
    updatedProduct.brandId = matchingBrand.id;
    updatedProduct.brand = matchingBrand.name;
  }

  onProductChange({ ...updatedProduct });
}

/**
 * Generic method that is in charge of updating the product from the inputs that the user changes
 *
 * @param {Product} product           - The original product when the user performs the change in an input
 * @param {Function} onProductChange  - Method used to set the local state of the product variable
 * @param {Event} event               - The event emitted by the input OnChange method
 * @param {String} fieldName          - The field name on which apply the change
 */
export function handleOnChangeProduct(product, onProductChange, event, fieldName) {
  const updatedFieldValue = event.target.value;

  const updatedProduct = { ...product, [fieldName]: updatedFieldValue };

  onProductChange({ ...updatedProduct });
}

/**
 * Handle change on a channel-specific price or VAT field inside product.channelPrices.
 * When the edited channel is the default one, also sync product.price / product.vatRate
 * so that the creation modal validation and production parameter pre-fill stay up to date.
 *
 * @param {Product}  product          - The current product
 * @param {Function} onProductChange  - Setter for the product state
 * @param {Event}    event            - The input change event
 * @param {String}   channelId        - The channel id to update
 * @param {String}   field            - 'priceWithTaxes' or 'vatRate'
 * @param {String}   defaultChannelId - The client's default channel id
 */
export function handleOnChangeChannelPrice(
  product,
  onProductChange,
  event,
  channelId,
  field,
  defaultChannelId,
) {
  const rawValue = event.target.value;
  const updatedValue = rawValue ? formatTextNumberInputValue(rawValue) : rawValue;

  const updatedChannelPrices = {
    ...product.channelPrices,
    [channelId]: {
      ...product.channelPrices?.[channelId],
      channelId,
      [field]: updatedValue,
    },
  };

  const updatedProduct = { ...product, channelPrices: updatedChannelPrices };

  const priceHT = utils.getProductPriceExcludingVAT(updatedProduct, channelId);

  const updatedPriceHTByChannelId = {
    ...updatedProduct.priceHTByChannelId,
    [channelId]: priceHT,
  };

  if (channelId === defaultChannelId) {
    if (field === 'priceWithTaxes') {
      updatedProduct.price = updatedValue;
      updatedProduct.priceWithTaxes = updatedValue;
    }
    if (field === 'vatRate') {
      updatedProduct.vatRate = updatedValue;
    }
  }

  onProductChange({ ...updatedProduct, priceHTByChannelId: updatedPriceHTByChannelId });
}

export const handleCategorySubcategoryCreation = (propertyName, props) => {
  const { setItemDropdownPropertyName, setOpenModalAddNewItemDropdown } = props;
  setItemDropdownPropertyName(propertyName);

  setOpenModalAddNewItemDropdown(true);
};

export default {
  // Inputs
  ProductNameInput,
  ProductCategoryInput,
  ProductSubCategoryInput,
  ProductDlcInput,
  ProductChannelPriceInput,
  ProductChannelVatInput,
  ProductCurrencyInput,
  ProductSkuInput,
  ProductBrandsInput,
  // Handlers methods
  handleBrandInput,
  handleOnChangeProduct,
  handleOnChangeChannelPrice,
};
